import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, getConsoleConnections } from "./api";
import { getSessionChanges, listAllChatSessions, type ChatSession, type ChatConnection } from "./chat-api";
import { workbenchScope } from "./workbench-storage";

export interface SessionCatalog { epoch: string; cursor: string; sessions: ChatSession[]; incremental: boolean; generation: number }
export const orderSessions = (sessions: ChatSession[]) => sessions.sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned) || b.last_activity_at.localeCompare(a.last_activity_at) || b.id.localeCompare(a.id));
export function catalogKey(connection = getConsoleConnections()) { return ["chat-sessions", "catalog", workbenchScope(connection)] as const; }

/** Commit a complete bounded-watermark traversal to the query cache at once.
 * On failure, React Query retains the last snapshot/cursor, so retry cannot skip
 * pages. Credentials are captured once, never switched mid-pagination. */
export async function syncSessionCatalog(previous: SessionCatalog | undefined, connection: ChatConnection, signal?: AbortSignal): Promise<SessionCatalog> {
  let cursor = previous?.incremental ? previous.cursor : "0";
  let epoch = previous?.incremental ? previous.epoch : undefined;
  let through: string | undefined;
  let resets = 0;
  const generation = previous?.generation ?? 0;
  let sessions: Map<string, ChatSession> | undefined;
  for (;;) {
    let page;
    try { page = await getSessionChanges({ epoch, after: cursor, through }, connection, signal); }
    catch (error) {
      if (!(error instanceof ApiError) || error.status !== 404 || through !== undefined) throw error;
      // Older servers remain readable, with an explicit slow-sync disclosure.
      const active = await listAllChatSessions({}, connection, signal);
      const archived = await listAllChatSessions({ archived: true }, connection, signal);
      return { epoch: "", cursor: "0", sessions: orderSessions([...new Map([...active, ...archived].map(session => [session.id, session])).values()]), incremental: false, generation: generation + Number(previous?.incremental === true) };
    }
    if (!page || typeof page.epoch !== "string" || page.epoch.length > 64 || !validCursor(page.cursor) || !validCursor(page.through) || !Array.isArray(page.changes) || page.changes.length > 200 || typeof page.reset !== "boolean" || typeof page.has_more !== "boolean") throw new Error("Invalid session catalog response. Reload after checking the control server.");
    if (page.reset) {
      if (++resets > 3) throw new Error("Session catalog keeps resetting. Try again when the server is stable.");
      sessions = new Map(); cursor = "0"; through = undefined;
    } else if (epoch !== page.epoch || (through !== undefined && through !== page.through)) throw new Error("Session catalog changed during pagination. Reload to retry safely.");
    if (BigInt(page.cursor) < BigInt(cursor) || BigInt(page.cursor) > BigInt(page.through)
      || (page.has_more ? BigInt(page.cursor) === BigInt(cursor) || page.cursor === page.through || !page.changes.length : page.cursor !== page.through)) throw new Error("Session catalog pagination made no progress. Reload after checking the control server.");
    for (const change of page.changes) {
      if (!change || typeof change.id !== "string" || !change.id || (change.session !== null && (!validSession(change.session) || change.session.id !== change.id))) throw new Error("Invalid session catalog response. Reload after checking the control server.");
      sessions ??= new Map(previous?.sessions.map(session => [session.id, session]));
      if (change.session) sessions.set(change.id, change.session); else sessions.delete(change.id);
    }
    epoch = page.epoch; cursor = page.cursor; through = page.through;
    if (!page.has_more) {
      if (!sessions && previous?.incremental && previous.epoch === epoch && previous.cursor === cursor) return previous;
      return { epoch, cursor, sessions: sessions ? orderSessions([...sessions.values()]) : previous?.sessions ?? [], incremental: true, generation: generation + Number(resets > 0) };
    }
  }
}

function validCursor(value: unknown): value is string {
  return typeof value === "string" && /^(0|[1-9][0-9]{0,18})$/.test(value) && BigInt(value) <= 9223372036854775807n;
}
function validSession(value: unknown): value is ChatSession {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return ["id", "agent_name", "runtime_kind", "worker_name", "status", "created_at", "updated_at", "last_activity_at"].every(key => typeof row[key] === "string")
    && ["title", "working_directory", "source_directory", "pending_review_turn_id", "last_turn_status"].every(key => row[key] == null || typeof row[key] === "string")
    && (row.pinned == null || typeof row.pinned === "boolean");
}

export function useSessionCatalog(poll = false) {
  const client = useQueryClient();
  const connection = getConsoleConnections();
  const key = catalogKey(connection);
  return useQuery({
    queryKey: key,
    queryFn: ({ signal }) => syncSessionCatalog(client.getQueryData<SessionCatalog>(key), connection, signal),
    enabled: !!connection.controlToken,
    staleTime: query => query.state.data?.incremental === false ? 60_000 : 4000,
    // ActivityInbox owns the single timer; rail/compare share the same query.
    refetchInterval: query => poll ? (query.state.data?.incremental === false ? 60_000 : 5000) : false,
    refetchIntervalInBackground: true,
  });
}
